/**
 * useObservabilitySocket
 *
 * Bridges Socket.IO build/deploy events into the client-side observabilityService
 * producers so the Observability Dashboard reflects live runtime activity.
 *
 * Mount once inside the authenticated Layout (or alongside useSocketConnection).
 * Cleanup is automatic on unmount.
 */

import { useEffect } from "react";
import { socketService } from "@/services/socketService";
import {
  pushLog,
  pushMetric,
  pushDeployEvent,
  trackError,
} from "@/services/observabilityService";

export function useObservabilitySocket(): void {
  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // Build logs → observability log stream
    unsubs.push(
      socketService.on("build:log", (e) => {
        const level =
          e.level === "error" ? "error" :
          e.level === "warn"  ? "warn"  :
          e.level === "debug" ? "debug" : "info";
        pushLog(level, "build-service", e.message, {
          projectId: e.projectId,
          stage:     e.stage,
        });
      }),
    );

    // Build complete → deploy event + metrics
    unsubs.push(
      socketService.on("build:complete", (e) => {
        const now = Date.now();
        pushDeployEvent({
          id:          `build-${e.projectId}-${e.timestamp}`,
          projectName: e.projectId,
          environment: "production",
          status:      e.success ? "success" : "failed",
          startedAt:   now - e.duration,
          finishedAt:  now,
          durationMs:  e.duration,
          commit:      "",
          logs:        e.publicUrl ? [`Preview: ${e.publicUrl}`] : [],
        });

        if (e.success) {
          pushMetric("build.duration_ms", e.duration, "ms", "build-service");
          pushLog("info", "build-service", `Build complete - ${e.publicUrl ?? "no url"}`, {
            projectId: e.projectId,
          });
        }
      }),
    );

    // Build errors → error tracker
    unsubs.push(
      socketService.on("build:error", (e) => {
        trackError(
          e.message,
          `Build stage: ${e.stage ?? "unknown"}`,
          `/api/build`,
        );
        pushLog("error", "build-service", `Build failed: ${e.message}`, {
          projectId: e.projectId,
          stage:     e.stage,
        });
      }),
    );

    // Deployment progress → log stream
    unsubs.push(
      socketService.on("deployment:progress", (e) => {
        pushLog("info", "deploy-service", e.message, {
          deploymentId: e.deploymentId,
          projectId:    e.projectId,
          stage:        e.stage,
          percentage:   e.percentage,
        });
      }),
    );

    // Deployment complete
    unsubs.push(
      socketService.on("deployment:complete", (e) => {
        const now = Date.now();
        pushDeployEvent({
          id:          `deploy-${e.deploymentId}`,
          projectName: e.projectId,
          environment: "production",
          status:      e.success ? "success" : "failed",
          startedAt:   now - e.duration,
          finishedAt:  now,
          durationMs:  e.duration,
          commit:      "",
          logs:        e.publicUrl ? [`URL: ${e.publicUrl}`] : [],
        });
        pushMetric("deploy.duration_ms", e.duration, "ms", "deploy-service");
      }),
    );

    // Deployment failures
    unsubs.push(
      socketService.on("deployment:failed", (e) => {
        const now = Date.now();
        trackError(
          e.error,
          `Deployment stage: ${e.stage ?? "unknown"}`,
          `/api/deploy`,
        );
        pushDeployEvent({
          id:          `deploy-${e.deploymentId}`,
          projectName: e.projectId,
          environment: "production",
          status:      "failed",
          startedAt:   e.timestamp,
          finishedAt:  now,
          commit:      "",
          logs:        [e.error],
        });
      }),
    );

    return () => unsubs.forEach((u) => u());
  }, []);
}
